import { useCallback, useEffect, useRef, useState } from 'react';
import { useAPI } from '../contexts/APIContext';
import {
  attachStream,
  fetchPlayback,
  muxKnownOff,
  rememberMuxOff,
  setSourceKeepingPlay,
  startMuxData,
  watchAudioTracks,
} from '../lib/muxStreaming';
import logger from '../utils/logger';

// How long to wait for /playback before starting the MP4 anyway. The call is
// normally ~100ms; this only bounds a slow or unreachable backend.
const PLAYBACK_WAIT_MS = 2500;

/**
 * Owns the `src` of a <video>: asks the backend how to play `contentId` and
 * attaches Mux HLS when it's ready, otherwise the MP4 `fallbackUrl`.
 *
 * The <video> must NOT carry its own src / <source>; this hook sets it once,
 * after the answer, so the browser never downloads the MP4 and the stream.
 *
 * lazy: the video is a paused preview (story page); stream media only loads
 * once the viewer presses play.
 *
 * Returns { mode: 'pending' | 'mux' | 'mp4', playback } where playback is the
 * backend's `mux` block (poster, storyboard, scenes, key moments...) or null.
 */
export function useStreamingSource(videoRef, {
  contentId,
  fallbackUrl,
  shareToken = null,
  enabled = true,
  lazy = false,
  playerName = 'pixley-web',
} = {}) {
  const { apiClient } = useAPI();
  const [state, setState] = useState({ mode: 'pending', playback: null });
  // Dubbed audio languages on the Mux stream (empty when there's just one).
  const [audio, setAudio] = useState({ tracks: [], current: null });
  const audioCtl = useRef(null);
  // reload(): re-open the stream at the same spot (a new dub was just added
  // to it; the player only learns about new tracks when it loads the stream).
  const [reloadKey, setReloadKey] = useState(0);
  const resumeRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);

  // Follow the element itself, not just our inputs: if the page remounts the
  // <video> for the same story, the new element needs its source too.
  // Runs after every commit on purpose; it only sets state when the element
  // actually changed, so it can't loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (videoRef.current !== videoEl) setVideoEl(videoRef.current);
  });

  useEffect(() => {
    const video = videoEl;
    if (!enabled || !video) return undefined;
    if (!fallbackUrl && !contentId) return undefined;

    let cancelled = false;
    let stream = null;
    let stopData = () => {};
    let lastAnswer = null;
    const initTime = Date.now();
    setState({ mode: 'pending', playback: null });
    setAudio({ tracks: [], current: null });

    const playMp4 = () => {
      if (cancelled || !fallbackUrl) return;
      if (video.getAttribute('src') !== fallbackUrl) setSourceKeepingPlay(video, fallbackUrl);
      setState({ mode: 'mp4', playback: null });
    };

    // First real play of a story that isn't on Mux yet: ask for the copy
    // (next viewer gets the stream). Page views alone never trigger ingest.
    const onFirstPlay = () => {
      video.removeEventListener('play', onFirstPlay);
      if (cancelled || !contentId || !lastAnswer) return;
      if (lastAnswer.mode === 'mux' || lastAnswer.status === 'disabled') return;
      fetchPlayback(apiClient, contentId, shareToken, { create: true });
    };
    video.addEventListener('play', onFirstPlay);

    if (!contentId || muxKnownOff()) {
      playMp4();
      return () => {
        cancelled = true;
        video.removeEventListener('play', onFirstPlay);
      };
    }

    const timeout = new Promise((resolve) => setTimeout(() => resolve('timeout'), PLAYBACK_WAIT_MS));

    (async () => {
      const answer = await Promise.race([fetchPlayback(apiClient, contentId, shareToken), timeout]);
      if (cancelled) return;
      lastAnswer = answer && answer !== 'timeout' ? answer : null;
      if (lastAnswer?.status === 'disabled') rememberMuxOff();
      const mux = lastAnswer?.mode === 'mux' ? lastAnswer.mux : null;
      if (!mux?.hls_url) {
        playMp4();
        return;
      }
      stream = await attachStream(video, {
        hlsUrl: mux.hls_url,
        fallbackUrl,
        lazy,
        isCancelled: () => cancelled,
        onFallback: (reason) => {
          logger.warn('Mux stream fell back to MP4:', reason);
          if (!cancelled) setState({ mode: 'mp4', playback: null });
        },
      });
      if (cancelled) {
        stream.detach();
        return;
      }
      if (stream.engine === 'mp4' || stream.engine === 'cancelled') return;
      setState({ mode: 'mux', playback: mux });
      if (resumeRef.current) {
        const resume = resumeRef.current;
        resumeRef.current = null;
        const restore = () => {
          video.removeEventListener('loadedmetadata', restore);
          try { video.currentTime = resume.time; } catch (_) { /* not seekable yet */ }
          if (resume.playing) video.play().catch(() => {});
        };
        video.addEventListener('loadedmetadata', restore);
        if (resume.playing) video.play().catch(() => {});
      }
      audioCtl.current = watchAudioTracks(video, stream, (next) => {
        if (!cancelled) setAudio(next);
      });
      stopData = await startMuxData(video, {
        envKey: mux.data_env_key,
        videoId: contentId,
        playerName,
        hls: stream.hls,
        Hls: stream.Hls,
        initTime,
      });
      if (cancelled) stopData();
    })();

    return () => {
      cancelled = true;
      video.removeEventListener('play', onFirstPlay);
      stopData();
      if (audioCtl.current) {
        audioCtl.current.stop();
        audioCtl.current = null;
      }
      if (stream) stream.detach();
    };
    // playerName is a reporting label; changing it shouldn't reload media.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoEl, enabled, contentId, fallbackUrl, shareToken, lazy, apiClient, reloadKey]);

  const selectAudio = useCallback((id) => audioCtl.current?.select(id), []);
  const reload = useCallback(() => {
    const video = videoRef.current;
    if (video) resumeRef.current = { time: video.currentTime || 0, playing: !video.paused };
    setReloadKey((k) => k + 1);
  }, [videoRef]);
  return { ...state, audio, selectAudio, reload };
}

export default useStreamingSource;
